import React, { useEffect } from 'react'
import { useBlocker } from '@tanstack/react-router'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { ampli } from '@/shared/lib/ampli'

type NavigationPromptProps = {
	/**
	 * Flag to enable/disable prompt
	 */
	when: boolean

	/**
	 * Dialog message
	 */
	message: React.ReactNode

	/**
	 * Dialog title
	 */
	title: string

	/**
	 * Ok button text
	 */
	okText?: string

	/**
	 * Cancel button text
	 */
	cancelText?: string
}

export default function NavigationPrompt({
	when,
	message,
	title,
	okText = 'Ok',
	cancelText = 'Cancel',
}: NavigationPromptProps) {
	const { proceed, reset, status } = useBlocker({
		shouldBlockFn: () => when,
		withResolver: true,
	})

	useEffect(() => {
		if (status === 'blocked') {
			ampli.navigationPromptShow()
		}
	}, [status])

	return (
		<AlertDialog open={status === 'blocked'}>
			<AlertDialogContent data-testid='navigation-prompt'>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{message}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogAction
						autoFocus
						variant='outline'
						data-testid='navigation-prompt-cancel'
						onClick={() => {
							ampli.navigationPromptCancel()
							reset?.()
						}}
					>
						{cancelText}
					</AlertDialogAction>
					<AlertDialogAction
						data-testid='navigation-prompt-confirm'
						variant='primary'
						onClick={() => {
							ampli.navigationPromptConfirm()
							proceed?.()
						}}
					>
						{okText}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
